import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { X } from 'lucide-react';

const WHATSAPP_NUMBER = '918877664459'; // Without + sign for wa.me link
const DEFAULT_MESSAGE = "Hi! I'm interested in your IT training courses. Please share more details about available programs and placement assistance.";

// Landing page routes where WhatsApp button should be hidden
const HIDDEN_ON_ROUTES = [
  '/sap-successfactors',
  '/sap-successfactors-employee-central',
  '/sap-successfactors-rcm',
  '/sap-successfactors-lms',
  '/sap-successfactors-compensation',
  '/sap-successfactors-time-management',
  '/sap-successfactors-onboarding',
  '/sap-successfactors-story-reports',
  '/sap-successfactors-ecp',
];

export default function WhatsAppButton() {
  const location = useLocation();
  const [isHovered, setIsHovered] = useState(false);
  const [showTooltip, setShowTooltip] = useState(true);

  // Check if current route is a landing page
  const isLandingPage = HIDDEN_ON_ROUTES.some(route => 
    location.pathname === route || location.pathname.startsWith(route + '/')
  );

  // Hide tooltip after 5 seconds
  useEffect(() => {
    const timer = setTimeout(() => setShowTooltip(false), 5000);
    return () => clearTimeout(timer);
  }, []);

  // Don't render on landing pages
  if (isLandingPage) {
    return null;
  }

  const handleClick = () => {
    const encodedMessage = encodeURIComponent(DEFAULT_MESSAGE);
    const whatsappUrl = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodedMessage}`;
    window.open(whatsappUrl, '_blank');
    
    // Track with Meta Pixel if available
    if (window.fbq) {
      window.fbq('track', 'Contact', {
        content_name: 'WhatsApp Click',
        content_category: 'Lead'
      });
    }
  };

  return (
    <>
      {/* Floating WhatsApp Button */}
      <div className="fixed bottom-6 right-6 z-50 flex flex-col items-end gap-3">
        {/* Tooltip */}
        {(showTooltip || isHovered) && (
          <div 
            className="bg-white rounded-lg shadow-lg p-3 max-w-[200px] animate-fade-in relative"
            style={{
              animation: 'fadeIn 0.3s ease-out'
            }}
          >
            <button 
              onClick={() => setShowTooltip(false)}
              className="absolute -top-2 -right-2 w-5 h-5 bg-slate-200 rounded-full flex items-center justify-center hover:bg-slate-300 transition-colors"
            >
              <X size={12} className="text-slate-600" />
            </button>
            <p className="text-sm text-slate-700 font-medium">Need help? Chat with us!</p>
            <p className="text-xs text-slate-500 mt-1">Click to start a WhatsApp conversation</p>
            {/* Arrow */}
            <div className="absolute -bottom-2 right-6 w-4 h-4 bg-white transform rotate-45 shadow-lg"></div>
          </div>
        )}

        {/* WhatsApp Button */}
        <button
          onClick={handleClick}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          data-testid="whatsapp-floating-btn"
          className="group flex items-center justify-center w-14 h-14 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-110"
          style={{
            background: 'linear-gradient(135deg, #25D366 0%, #128C7E 100%)',
          }}
          aria-label="Chat on WhatsApp"
        >
          {/* Pulse animation ring */}
          <span 
            className="absolute w-14 h-14 rounded-full animate-ping opacity-30"
            style={{ backgroundColor: '#25D366' }}
          ></span>
          
          {/* WhatsApp Icon */}
          <svg 
            viewBox="0 0 24 24" 
            fill="white" 
            className="w-7 h-7 relative z-10"
          >
            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
          </svg>
        </button>
      </div>

      {/* Add keyframe animation */}
      <style>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </>
  );
}
